import { useState, useRef, useEffect } from 'react';
import * as XLSX from 'xlsx';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import {
  Upload, FileSpreadsheet, X, Mail, AlignLeft, Type, Play, Sparkles,
  CheckCircle2, AlertTriangle, Loader2, Info, ArrowRight, Check
} from 'lucide-react';
import { supabase } from '../supabaseClient';
import { fetchMyCredits } from '../lib/credits';
import { loadWithCache, getCached, setCached } from '../lib/cache';
import { notifyOrdersChanged, kickOrdersWatch } from '../lib/ordersWatch';
import { fetchOrderProgress, progressCounts } from '../lib/orderProgress';
import { fetchPricing, estimateCredits } from '../lib/pricing';
import { cn, formatNumber } from '../lib/utils';
import { Card, CardBody, CardHeader, CardTitle, CardDescription } from './ui/Card';
import Button from './ui/Button';
import { Input, Label } from './ui/Input';
import { Select, MultiSelect } from './ui/Select';
import Badge from './ui/Badge';
import MetricCard from './ui/MetricCard';
import PageHeader from './ui/PageHeader';
import { ALL_MARKETPLACES, ALL_LANGUAGES, MARKETPLACE_META } from './marketplaces';

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY;
const PROCESS_ORDER_URL = `${SUPABASE_URL}/functions/v1/process-order`;
const BASE_CATALOG_URL = `${SUPABASE_URL}/functions/v1/base-catalog`;
const SELLASIST_CATALOG_URL = `${SUPABASE_URL}/functions/v1/sellasist-catalog`;
const SYSTEM_LABEL = { baselinker: 'BaseLinker', sellasist: 'Sellasist' };
const ETA_SECONDS_PER_PRODUCT = 10;

// K2 (plan v6, 2026-10-14): zlecenie dla klienta na kanał. Kanał = platforma × rynek × język;
// do istniejącego pipeline'u tłumaczymy go na `marketplace` + `language` (adaptery treści),
// a `tenant_id` + `channel_id` lecą obok, żeby silnik wiedział, na czyje konto to idzie.
const CHANNEL_PLATFORM_TO_MARKETPLACE = { kaufland: 'Kaufland', allegro: 'Allegro', amazon: 'Amazon' };
const CHANNEL_LANGUAGE_LABEL = { de: 'Niemiecki', pl: 'Polski', cs: 'Czeski', sk: 'Słowacki', en: 'Angielski', fr: 'Francuski', it: 'Włoski', es: 'Hiszpański', nl: 'Niderlandzki', ro: 'Rumuński', hu: 'Węgierski' };
const MARKET_LABEL = { de: 'Niemcy', at: 'Austria', cz: 'Czechy', sk: 'Słowacja', pl: 'Polska', fr: 'Francja', it: 'Włochy', es: 'Hiszpania', nl: 'Holandia', ro: 'Rumunia', hu: 'Węgry', en: 'UK' };
const CHANNEL_PLATFORM_LABEL = { kaufland: 'Kaufland', allegro: 'Allegro', amazon: 'Amazon', base_channel: 'BaseLinker', sellasist_channel: 'Sellasist' };
// PROGRESS_CAP usunięty 2026-05-06 — używamy realnego % z dispatcher_health (single source of truth z LiveDashboard).

// ─── File parsing utilities (1:1 from legacy) ─────────────────────
function parseFileHeaders(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const wb = XLSX.read(data, { type: 'array' });
        const sheet = wb.Sheets[wb.SheetNames[0]];
        const json = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '' });
        const headers = (json[0] || []).map(h => String(h).trim()).filter(Boolean);
        const rows = json.slice(1).filter(row => row.some(c => c !== null && c !== undefined && String(c).trim() !== '')).length;
        resolve({ headers, rows });
      } catch (err) { reject(err); }
    };
    reader.onerror = reject;
    reader.readAsArrayBuffer(file);
  });
}

function stripHtml(html) {
  if (!html || html === '') return '';
  html = String(html);
  html = html.replace(/<li[^>]*>/gi, '\n- ');
  html = html.replace(/<(p|div|h1|h2|h3|h4|br)[^>]*>/gi, '\n');
  let text = html.replace(/<[^>]*>/g, '');
  text = text
    .replace(/&nbsp;/gi, ' ').replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<').replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"').replace(/&#39;/gi, "'")
    .replace(/&mdash;/gi, '—').replace(/&ndash;/gi, '–')
    .replace(/&euro;/gi, '€').replace(/&deg;/gi, '°');
  return text.replace(/[ \t]+/g, ' ').replace(/\n\s*\n/g, '\n').trim();
}

function containsHtml(text) {
  if (!text || typeof text !== 'string') return false;
  return /<\/?[a-z][\s\S]*>/i.test(text);
}

function parseFileProducts(file, nameCol, descCol) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const wb = XLSX.read(data, { type: 'array' });
        const sheet = wb.Sheets[wb.SheetNames[0]];
        const json = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '' });
        const headers = (json[0] || []).map(h => String(h).trim());
        const rows = json.slice(1).filter(row => row.some(c => c !== null && c !== undefined && String(c).trim() !== ''));
        const products = rows.map(row => {
          const product = {};
          headers.forEach((h, i) => { product[h] = row[i] ?? ''; });
          const rawName = product[nameCol] || '';
          const rawDesc = product[descCol] || '';
          product.nazwa_clean = containsHtml(rawName) ? stripHtml(rawName) : String(rawName);
          product.opis_clean  = containsHtml(rawDesc) ? stripHtml(rawDesc) : String(rawDesc);
          return product;
        });
        resolve(products);
      } catch (err) { reject(err); }
    };
    reader.onerror = reject;
    reader.readAsArrayBuffer(file);
  });
}

/** T23: wiersze z BaseLinkera (edge base-catalog) → ten sam kształt produktu, co z pliku. */
function rowsToProducts(headers, rows, nameCol, descCol) {
  return rows.map(row => {
    const product = {};
    headers.forEach((h, i) => { product[h] = row[i] ?? ''; });
    const rawName = product[nameCol] || '';
    const rawDesc = product[descCol] || '';
    product.nazwa_clean = containsHtml(rawName) ? stripHtml(rawName) : String(rawName);
    product.opis_clean  = containsHtml(rawDesc) ? stripHtml(rawDesc) : String(rawDesc);
    return product;
  });
}

// ─── Marketplace logo tile ────────────────────────────────────────
function MarketplaceLogo({ name, size = 40 }) {
  const meta = MARKETPLACE_META[name] || { initial: '?', color: '#71717A', textColor: '#fff' };
  return (
    <div
      className="rounded-lg flex items-center justify-center font-bold flex-shrink-0 shadow-sm"
      style={{
        width: size,
        height: size,
        background: meta.color,
        color: meta.textColor,
        fontSize: Math.round(size * 0.45),
        letterSpacing: '-0.02em',
      }}
    >
      {meta.initial}
    </div>
  );
}

// ─── Marketplace card (selectable tile) ───────────────────────────
function MarketplaceCard({ name, selected, languages, onToggle, onLanguagesChange }) {
  const meta = MARKETPLACE_META[name] || {};
  return (
    <motion.div
      whileTap={{ scale: 0.97 }}
      transition={{ duration: 0.12 }}
      className={cn(
        'group relative rounded-xl border-2 transition-all duration-200 ease-gentle',
        selected
          ? 'border-brand-600 bg-brand-50/40 dark:bg-brand-900/20 shadow-md shadow-brand-600/10 z-10'
          : 'border-ink-200 bg-white hover:border-ink-300 hover:shadow-card-hover hover:-translate-y-0.5 dark:border-ink-700 dark:bg-ink-900 dark:hover:border-ink-600'
      )}
    >
      <button
        type="button"
        onClick={onToggle}
        className="w-full text-left p-3.5 flex items-center gap-3"
      >
        <MarketplaceLogo name={name} size={42} />
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-sm text-ink-900 dark:text-ink-50 tracking-tightish">{name}</div>
          <div className="text-2xs text-ink-500 dark:text-ink-400 mt-0.5">{meta.region}</div>
        </div>
        <div className={cn(
          'w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all flex-shrink-0',
          selected
            ? 'border-brand-600 bg-brand-600'
            : 'border-ink-300 bg-white group-hover:border-ink-400 dark:border-ink-600 dark:bg-ink-900 dark:group-hover:border-ink-500'
        )}>
          {selected && <Check className="w-3 h-3 text-white" strokeWidth={3.5} />}
        </div>
      </button>

      {selected && (
        <div className="px-3.5 pb-3.5 pt-1 animate-fade-in">
          <Label className="text-2xs uppercase tracking-wide text-ink-500 dark:text-ink-400 font-bold mb-1.5">
            Języki ({languages.length})
          </Label>
          <MultiSelect
            values={languages}
            onChange={onLanguagesChange}
            options={ALL_LANGUAGES}
            placeholder="Wybierz języki..."
            maxChipsVisible={6}
          />
        </div>
      )}
    </motion.div>
  );
}

// ─── Queue progress view (active jobs in flight) ──────────────────
//
// Wzorzec 1:1 z LiveDashboard — TEN SAM source of truth + IDENTYCZNA formuła:
// - dispatcher_health view zamiast batch_jobs (eliminuje rozjazd z LiveDashboard)
// - Realtime channel na batch_jobs + 5s safety poll wywołują refresh dispatcher_health
// - Procent = productsDone / productsTotal × 100 (per-PRODUKT, identycznie z radial gauge)
// - Bug fix 2026-05-06: zmiana source na dispatcher_health (było `batch_jobs[0].total_batches`)
// - Bug fix 2026-05-11: zmiana formuły z `done/total_batches` (per-batch) na
//   `results_saved/product_count` (per-produkt) — wcześniejszy fix zmienił tylko źródło danych,
//   formuła została per-batch i wciąż rozjeżdżała się z LiveDashboard (np. 5% vs 4% gdy
//   walidator odrzucił 1/5 produktów w batchu).
//
function QueueProgressView({ activeJobs, onDone, onBalanceChange, onOpenReadiness = null }) {
  const [jobs, setJobs] = useState(activeJobs);
  // A3: anulowanie zlecenia. confirmCancel = order_id, dla którego pokazujemy „Na pewno?".
  const [confirmCancel, setConfirmCancel] = useState(null);
  const [cancelling, setCancelling] = useState(null);

  // Jedno miejsce nakładania stanu zlecenia z bazy (realtime ORAZ poll).
  const applyOrder = (o) => setJobs(prev => prev.map(j =>
    j.order_id === o.id
      ? { ...j, status: o.status, status_reason: o.status_reason, products_done: o.products_done, products_failed: o.products_failed }
      : j
  ));

  // Status zlecenia czytany też POLLEM, nie tylko z realtime: zgubione zdarzenie realtime
  // zostawiało zakończone zlecenie na „Procesowanie" do odświeżenia strony.
  const refreshOrders = async (ids) => {
    if (!ids.length) return;
    const { data } = await supabase
      .from('orders')
      .select('id, status, status_reason, products_done, products_failed')
      .in('id', ids);
    (data || []).forEach(applyOrder);
  };

  const handleCancel = async (job) => {
    setCancelling(job.order_id);
    const { error } = await supabase.rpc('cancel_order', { p_order_id: job.order_id });
    if (error) {
      if (/order_not_cancellable/.test(error.message || '')) toast.info('To zlecenie już się zakończyło.');
      else toast.error('Nie udało się anulować zlecenia: ' + (error.message || 'nieznany błąd'));
    } else {
      toast.success('Zlecenie anulowane. Kredyty za niewygenerowane produkty wróciły na konto.');
    }
    await refreshOrders([job.order_id]);
    if (onBalanceChange) onBalanceChange();
    setCancelling(null);
    setConfirmCancel(null);
  };
  // progressData[order_id] = { done, processing, pending, failed, total, productsDone }
  // PERF-UX (23.10): ostatni znany postęp z cache — paski nie startują od 0 % przy ponownym wejściu.
  const [progressData, setProgressData] = useState(() => getCached('neworder:progress')?.data || {});
  useEffect(() => {
    const ids = new Set(jobs.map(j => j.order_id));
    const keep = Object.fromEntries(Object.entries(progressData).filter(([id]) => ids.has(id)));
    if (Object.keys(keep).length) setCached('neworder:progress', keep, { persist: false });
    // eslint-disable-next-line
  }, [progressData]);
  useEffect(() => {
    const refreshProgress = async (orderId) => {
      // 22.10: RPC order_progress (per produkt z engine_products; ten sam co Live Dashboard), fallback: dispatcher_health.
      const { data, error } = await fetchOrderProgress(orderId);

      if (error) { console.warn('[QueueProgress] error:', error); return; }
      if (!data) return;

      const { total, done, processing, pending, failed, productsDone, productsTotal } = progressCounts(data);
      const pctProducts = productsTotal > 0 ? (productsDone / productsTotal) * 100 : 0;
      console.log(`[QueueProgress] ${orderId.slice(0, 8)}: ${productsDone}/${productsTotal} produktów = ${pctProducts.toFixed(1)}%  (batches: ${done}/${total}, proc=${processing} pend=${pending} fail=${failed})`);
      setProgressData(p => ({ ...p, [orderId]: { done, processing, pending, failed, total, productsDone, productsTotal } }));
    };

    // B1 (audyt bazy 20.10): bez kanałów Realtime (postgres_changes każe Supabase odpytywać WAL w kółko, także gdy nic
    // się nie dzieje — połowa ruchu na prod). Zostaje odpytywanie postępu (order_progress): co 15 s, tylko gdy coś jest w toku
    // i tylko gdy karta jest widoczna.
    const orderIds = jobs.map(j => j.order_id);
    const refreshAll = () => { jobs.forEach(j => refreshProgress(j.order_id)); refreshOrders(orderIds); };
    refreshAll();
    const active = jobs.some(j => j.status !== 'done' && j.status !== 'error');
    if (!active) return undefined;
    const tick = () => { if (document.visibilityState !== 'hidden') refreshAll(); };
    const pollInterval = setInterval(tick, 15000); // 22.10: 5 s → 15 s (baza Nano)
    document.addEventListener('visibilitychange', tick);

    return () => {
      clearInterval(pollInterval);
      document.removeEventListener('visibilitychange', tick);
    };
  }, [jobs.map(j => `${j.order_id}:${j.status}`).join(',')]);

  const allFinished = jobs.every(j => j.status === 'done' || j.status === 'error');
  const readinessJob = jobs.find(j => j.tenant_id && j.status === 'done') || null;
  // Zamknięcie zlecenia może zwrócić kredyty (produkty bez treści, błąd startu) — odśwież saldo.
  useEffect(() => {
    if (allFinished && onBalanceChange) onBalanceChange();
    // eslint-disable-next-line
  }, [allFinished]);
  const totalProducts = jobs.reduce((sum, j) => sum + (j.product_count || 0), 0);
  const totalCredits = jobs.reduce((sum, j) => sum + (j.credits_used || 0), 0);
  const doneCount = jobs.filter(j => j.status === 'done').length;
  const errorCount = jobs.filter(j => j.status === 'error').length;

  return (
    <div className="animate-fade-in">
      <PageHeader
        title="Kolejka zleceń"
        badge={
          <>
            {!allFinished && <Badge variant="brand" size="md" dot>Procesowanie</Badge>}
            {allFinished && errorCount === 0 && <Badge variant="success" size="md">Zakończone</Badge>}
            {allFinished && errorCount > 0 && <Badge variant="warning" size="md">Z błędami</Badge>}
          </>
        }
        subtitle={
          allFinished
            ? (errorCount > 0
                ? 'Zakończone — część zleceń się nie powiodła, powód jest przy zleceniu. Gotowe pliki pobierzesz w zakładce Historia.'
                : (readinessJob ? 'Wszystkie zlecenia zakończone. Następny krok: Gotowość — kategorie, GPSR i „Publikuj”. Plik pobierzesz też w Historii.' : 'Wszystkie zlecenia zakończone. Pliki pobierzesz w zakładce Historia.'))
            : 'Zlecenia przetwarzane kolejno — możesz zamknąć tę kartę, gotowe pliki znajdziesz w zakładce Historia.'
        }
        action={
          allFinished && (
            <div className="flex items-center gap-2">
              {/* T2.1: zlecenie klienta kończy się w Gotowości (kategorie, GPSR, Publikuj) — wcześniej nic tam nie prowadziło. */}
              {onOpenReadiness && readinessJob && (
                <Button variant="primary" size="md" onClick={() => onOpenReadiness(readinessJob.order_id)} rightIcon={<ArrowRight className="w-4 h-4" />}>
                  Dalej: Gotowość i publikacja
                </Button>
              )}
              <Button variant={onOpenReadiness && readinessJob ? 'secondary' : 'primary'} size="md" onClick={onDone} rightIcon={<ArrowRight className="w-4 h-4" />}>
                Nowe zlecenie
              </Button>
            </div>
          )
        }
      />

      {/* KPI strip */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <MetricCard label="Produkty"   value={formatNumber(totalProducts)} />
        <MetricCard label="Zleceń"     value={`${doneCount}/${jobs.length}`} sub={jobs.length > 1 ? 'ukończonych' : ''} />
        <MetricCard label="Kredyty"    value={formatNumber(totalCredits)} />
        <MetricCard label="Plik wynikowy" value="Zakładka Historia" small />
      </div>

      {/* Job queue */}
      <div className="space-y-2">
        {jobs.map((job, i) => {
          const isDone = job.status === 'done';
          const isError = job.status === 'error';
          const isActive = job.status === 'processing';
          const isCancelled = isError && /^Anulowane/.test(job.status_reason || '');
          const canCancel = !isDone && !isError;
          const pd = progressData[job.order_id];
          let pct = 0;
          // Per-PRODUKT (identycznie z LiveDashboard RadialProgress, linia ~337):
          // pct = productsDone / productsTotal × 100. productsTotal bierzemy z job.product_count
          // (orders.product_count w momencie wrzucenia do kolejki — stabilne, nie zmienia się
          // po retry/rechunking jak batch_jobs.total_batches mogłoby).
          if (pd && job.product_count > 0) pct = (pd.productsDone / job.product_count) * 100;
          if (isDone) pct = 100;
          // Bez cap — używamy realnej wartości z dispatcher_health (ten sam source of truth co LiveDashboard).
          pct = Math.min(Math.max(pct, 0), 100);
          return (
            <div
              key={job.order_id}
              className={cn(
                'card p-4 flex items-center gap-4 transition-all',
                isActive && 'ring-2 ring-brand-200/60 border-brand-200',
                isDone && 'bg-success-light/20 border-success/20',
                isError && !isCancelled && 'bg-danger-light/20 border-danger/20',
                isCancelled && 'opacity-80',
              )}
            >
              <div className={cn(
                'w-9 h-9 rounded-lg flex items-center justify-center text-sm font-bold flex-shrink-0',
                isDone   && 'bg-success text-white',
                isError && !isCancelled && 'bg-danger  text-white',
                isActive && 'bg-brand-600 text-white',
                (isCancelled || (!isDone && !isError && !isActive)) && 'bg-ink-100 dark:bg-ink-800 text-ink-500 dark:text-ink-400',
              )}>
                {isDone ? <Check className="w-5 h-5" strokeWidth={3} /> : isError ? <X className="w-5 h-5" strokeWidth={3} /> : i + 1}
              </div>

              <MarketplaceLogo name={job.marketplace} size={36} />

              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-ink-900 dark:text-ink-50">{job.marketplace}</div>
                <div className="text-xs text-ink-500 dark:text-ink-400 mt-0.5">{job.language} · {formatNumber(job.product_count)} produktów</div>
                {job.status_reason && (
                  <div className={cn(
                    'text-xs mt-1',
                    isCancelled ? 'text-ink-500 dark:text-ink-400'
                      : isError ? 'text-danger-dark dark:text-danger'
                      : 'text-warning-dark dark:text-warning'
                  )}>
                    {job.status_reason}
                  </div>
                )}
              </div>

              <div className="hidden md:block w-32">
                <div className="h-1.5 bg-ink-100 dark:bg-ink-800 rounded-full overflow-hidden">
                  <div
                    className={cn(
                      'h-full rounded-full transition-[width,background] duration-500',
                      isDone   && 'bg-success',
                      isError && !isCancelled && 'bg-danger',
                      isActive && 'bg-brand-600',
                      (isCancelled || (!isDone && !isError && !isActive)) && 'bg-ink-300',
                    )}
                    style={{ width: `${isDone ? 100 : pct}%` }}
                  />
                </div>
              </div>

              {canCancel && (
                <div className="flex items-center gap-1.5 flex-shrink-0">
                  {confirmCancel === job.order_id ? (
                    <>
                      <Button size="xs" variant="danger" loading={cancelling === job.order_id} onClick={() => handleCancel(job)}>
                        Tak, anuluj
                      </Button>
                      <Button size="xs" variant="ghost" disabled={cancelling === job.order_id} onClick={() => setConfirmCancel(null)}>
                        Nie
                      </Button>
                    </>
                  ) : (
                    <Button size="xs" variant="ghost" onClick={() => setConfirmCancel(job.order_id)} leftIcon={<X className="w-3 h-3" />}>
                      Anuluj
                    </Button>
                  )}
                </div>
              )}

              <div className="w-24 text-right">
                {isError && <Badge variant={isCancelled ? 'neutral' : 'danger'} size="md">{isCancelled ? 'Anulowane' : 'Błąd'}</Badge>}
                {isDone && <Badge variant="success" size="md">Gotowe</Badge>}
                {isActive && (
                  <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-brand-700 dark:text-brand-300 tabular-nums">
                    <Loader2 className="w-3 h-3 animate-spin" />
                    {pct.toFixed(1)}%
                  </span>
                )}
                {!isDone && !isError && !isActive && (
                  <Badge variant="neutral" size="md">W kolejce</Badge>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-5 flex items-center justify-between text-xs text-ink-500 dark:text-ink-400">
        <span>
          Problemy?{' '}
          <a href="mailto:kontakt@mediafy.com.pl" className="text-brand-700 dark:text-brand-300 font-semibold hover:underline">
            kontakt@mediafy.com.pl
          </a>
        </span>
        {!allFinished && (
          <span className="inline-flex items-center gap-1.5">
            <span className="live-dot" /> Realtime sync aktywny
          </span>
        )}
      </div>
    </div>
  );
}

// KpiTile zastąpiony przez MetricCard z ui/MetricCard.js

// T2.1: automatyczne rozpoznanie kolumn z nazwą i opisem (klient nie musi wybierać, gdy nagłówki są typowe).
const NAME_HEADERS = ['nazwa', 'nazwa produktu', 'name', 'product name', 'title', 'tytuł', 'tytul', 'produkt', 'nazwa towaru', 'bezeichnung', 'produktname'];
const DESC_HEADERS = ['opis', 'opis produktu', 'description', 'product description', 'opis długi', 'opis dlugi', 'beschreibung', 'long description', 'opis html'];
export function guessColumn(headers, candidates, exclude = '') {
  const norm = (h) => String(h || '').trim().toLowerCase().replace(/[_\-]+/g, ' ').replace(/\s+/g, ' ');
  const list = (headers || []).filter(h => h !== exclude);
  for (const c of candidates) { const hit = list.find(h => norm(h) === c); if (hit) return hit; }
  for (const c of candidates) { const hit = list.find(h => norm(h).startsWith(c)); if (hit) return hit; }
  return '';
}

// ─── Main NewOrderPage ────────────────────────────────────────────
export default function NewOrderPage({ user, credits, onCreditsChange, onOpenReadiness = null, initialTenantId = null, onInitialTenantUsed = null }) {
  const fileRef = useRef();
  // PERF-UX (23.10): zlecenia w toku z cache — drugie wejście renderuje się od razu (bez „Sprawdzanie aktywnych zleceń…”),
  // świeży stan w tle. Wpis unieważniony (zlecenie uruchomione/zakończone — lib/ordersWatch) = czekamy na sieć jak dotąd.
  const activeKey = `neworder:active:${user.id}`;
  const [activeJobs, setActiveJobs] = useState(() => { const c = getCached(activeKey); return c && c.at > 0 ? c.data : null; });
  // PERF (23.10 wieczór): bez blokującego „Sprawdzanie aktywnych zleceń…” — formularz od razu, a jeśli zapytanie znajdzie
  // zlecenie w toku, ekran przełącza się na postęp. Na bazie Nano to zapytanie trwało 0,6–9 s (edge_logs 23.10).
  const [checkingActive, setCheckingActive] = useState(false);
  // Audyt 24.10: po „Uruchom” spóźniona odpowiedź sprawdzenia zleceń w toku (start ekranu, do 9 s) nie może nadpisać
  // właśnie utworzonych zleceń (`createdJobs`) starą listą — flaga ustawiana na starcie wysyłki.
  const submittedRef = useRef(false);

  const [file, setFile] = useState(null);
  const [headers, setHeaders] = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [fileLoading, setFileLoading] = useState(false);
  const [dragging, setDragging] = useState(false);

  const [email, setEmail] = useState(user?.email || '');
  const [nameCol, setNameCol] = useState('');
  const [descCol, setDescCol] = useState('');
  const [aplusContent, setAplusContent] = useState(false);
  const [aplusImageCol, setAplusImageCol] = useState('');
  // Limit produktów na zlecenie A+ z konfiguracji (RPC aplus_max_products; brak → 5 jak w zajawce).
  const [aplusMax, setAplusMax] = useState(5);
  useEffect(() => {
    // PERF-UX: limit A+ z cache (zmienia się rzadko) — bez round-tripu przy każdym wejściu.
    loadWithCache('neworder:aplus_max', async () => { const { data } = await supabase.rpc('aplus_max_products'); return Number(data); }, (n) => { if (Number.isFinite(n) && n > 0) setAplusMax(n); }, { staleMs: 60 * 60_000 }).catch(() => {});
  }, []);
  const [pricing, setPricing] = useState(null);
  useEffect(() => { fetchPricing().then(setPricing).catch(() => {}); }, []);

  // Saldo jest read-only dla klienta — po zleceniu odczytujemy je z powrotem.
  const refreshBalance = async () => {
    const c = await fetchMyCredits(user.id);
    if (typeof c?.balance === 'number') onCreditsChange(c.balance);
  };

  // mp config: { [marketplaceName]: [languages...] }
  const [mpConfig, setMpConfig] = useState({});

  // Klienci (K2) — lista widoczna tylko dla admina (RLS); zwykły użytkownik nie zobaczy tej sekcji.
  const [tenants, setTenants] = useState([]);
  const [tenantId, setTenantId] = useState(initialTenantId || '');
  // Klient z kreatora użyty raz — kolejne „Nowe zlecenie” nie dziedziczy go po cichu.
  useEffect(() => { if (initialTenantId && onInitialTenantUsed) onInitialTenantUsed(); }, []);
  // T23: katalog z BaseLinkera zamiast pliku
  const [source, setSource] = useState('file');          // 'file' | 'base'
  const [baseConns, setBaseConns] = useState([]);        // połączenia baselinker (status ok) z nazwą klienta
  const [baseConnId, setBaseConnId] = useState('');
  const [baseCats, setBaseCats] = useState(null);        // {categories, counts, sampled}
  const [baseSel, setBaseSel] = useState([]);            // zaznaczone category_id
  const [baseBusy, setBaseBusy] = useState(false);
  const [baseData, setBaseData] = useState(null);        // {headers, rows}
  const [tenantChannels, setTenantChannels] = useState([]);
  const [selectedChannels, setSelectedChannels] = useState([]);
  // Życzenie Maćka (tor 1 → tor 2, 18.10 21:50): zlecenie zaczyna się od KLIENTA; „bez klienta” (sam plik) to druga, rzadsza ścieżka.
  const [noClient, setNoClient] = useState(false);
  // Bieżący wybór (dla odpowiedzi asynchronicznych, które wracają po zmianie klienta/połączenia).
  const liveSel = useRef({ tenantId: '', baseConnId: '' });
  liveSel.current = { tenantId, baseConnId };

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // PERF-UX: klienci i połączenia od razu z cache, świeże w tle (ten sam klucz co lista w Klienci nie — tu inne kolumny).
    loadWithCache('neworder:tenants', async () => { const { data, error } = await supabase.from('tenants').select('id, name, mode').order('name'); if (error) throw error; return data || []; }, (d) => setTenants(d || [])).catch(() => {});
    loadWithCache('neworder:baseconns', async () => { const { data, error } = await supabase.from('connections').select('id, tenant_id, system, meta, status').in('system', ['baselinker', 'sellasist']).eq('status', 'ok'); if (error) throw error; return data || []; }, (d) => setBaseConns(d || [])).catch(() => {});
  }, []);
  useEffect(() => {
    setBaseCats(null); setBaseSel([]);
    if (!baseConnId) return undefined;
    // PERF-UX (23.10): kategorie integratora (edge → API BaseLinkera/Sellasist, najwolniejsze żądanie ekranu) z cache:
    // od razu ostatnie drzewo, sieć dopiero gdy wpis starszy niż 5 min — wtedy w tle, bez blokowania kroku „Produkty”.
    let ignore = false; // szybka zmiana połączenia: spóźniona odpowiedź poprzedniego nie nadpisze kategorii
    const key = `neworder:basecats:${baseConnId}`;
    const cached = !!getCached(key);
    if (!cached) setBaseBusy(true);
    loadWithCache(key, async () => {
      const { data: { session } } = await supabase.auth.getSession();
      const c0 = baseConns.find(c => c.id === baseConnId);
      const res = await fetch(c0?.system === 'sellasist' ? SELLASIST_CATALOG_URL : BASE_CATALOG_URL, { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` }, body: JSON.stringify({ connection_id: baseConnId, action: 'categories' }) });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error || `HTTP ${res.status}`);
      return d;
    }, (d) => { if (!ignore) setBaseCats(d); }, { staleMs: 5 * 60_000 })
      .catch((e) => { if (!ignore && !cached) toast.error(`Integrator: ${e.message}`); })
      .finally(() => { if (!ignore && !cached) setBaseBusy(false); });
    return () => { ignore = true; if (!cached) setBaseBusy(false); };
  }, [baseConnId]);

  const baseConn = baseConns.find(c => c.id === baseConnId);
  const catalogUrl = baseConn?.system === 'sellasist' ? SELLASIST_CATALOG_URL : BASE_CATALOG_URL;
  const sysLabel = SYSTEM_LABEL[baseConn?.system] || 'Integrator';
  // Etykieta połączenia w obrębie klienta (bez nazwy klienta — klient jest już wybrany wyżej).
  const connLabel = (c) => `${SYSTEM_LABEL[c?.system] || c?.system}${c?.system === 'baselinker' ? ` · katalog #${c.meta?.inventory_id ?? '?'}` : ''}`;
  const tenantConns = baseConns.filter(c => c.tenant_id === tenantId);
  const fetchBaseProducts = async () => {
    // Odpowiedź z innego klienta/połączenia (użytkownik zmienił wybór w trakcie pobierania) jest odrzucana.
    const reqTenant = tenantId; const reqConn = baseConnId;
    const stale = () => liveSel.current.tenantId !== reqTenant || liveSel.current.baseConnId !== reqConn;
    setBaseBusy(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch(catalogUrl, { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` }, body: JSON.stringify({ connection_id: baseConnId, action: 'products', category_ids: baseSel, include_children: true, limit: 5000 }) });
      const d = await res.json();
      if (stale()) return;
      if (!res.ok) throw new Error(d.error || `HTTP ${res.status}`);
      if (!d.rows?.length) { toast.error(`${sysLabel} nie zwrócił produktów dla tego wyboru.`); return; }
      const conn = baseConns.find(c => c.id === reqConn);
      const tn = tenants.find(t => t.id === conn?.tenant_id);
      setBaseData({ headers: d.headers, rows: d.rows });
      setHeaders(d.headers); setTotalRows(d.rows.length);
      setFile({ name: `${sysLabel} · ${tn?.name || 'klient'} · ${baseSel.length ? baseSel.length + ' kat.' : 'cały katalog'} · ${d.rows.length} produktów`, virtual: true });
      setNameCol('Nazwa'); setDescCol('Opis');
      toast.success(`Pobrano ${d.rows.length} produktów (${sysLabel})`);
    } catch (e) { if (!stale()) toast.error(`${sysLabel}: ${e.message}`); }
    finally { setBaseBusy(false); }
  };
  useEffect(() => {
    setSelectedChannels([]);
    if (!tenantId) { setTenantChannels([]); return undefined; }
    let ignore = false; // szybka zmiana klienta: spóźniona odpowiedź poprzedniego nie nadpisze kanałów
    // PERF-UX: kanały klienta z cache (od razu), świeże w tle — zaznaczenie domyślne ustawiamy przy każdym `apply`.
    loadWithCache(`neworder:channels:${tenantId}`, async () => {
      const { data, error } = await supabase.from('channels').select('id, platform, market, language, enabled, settings').eq('tenant_id', tenantId).eq('enabled', true).order('platform');
      if (error) throw error;
      return data || [];
    }, (data) => {
      if (ignore) return;
      setTenantChannels(data || []);
      // T2.1: włączone kanały klienta są domyślnie zaznaczone (odznaczasz, czego nie chcesz) — wcześniej trzeba było klikać każdy.
      setSelectedChannels((data || []).filter(ch => CHANNEL_PLATFORM_TO_MARKETPLACE[ch.platform]).map(ch => ch.id));
    }).catch(() => {});
    return () => { ignore = true; };
  }, [tenantId]);
  // Źródło katalogu wynika z klienta: jego połączenie BaseLinker/Sellasist (pierwsze domyślnie), inaczej plik.
  useEffect(() => {
    const tc = baseConns.filter(c => c.tenant_id === tenantId);
    if (tenantId && tc.length) { setSource('base'); setBaseConnId(id => (tc.some(c => c.id === id) ? id : tc[0].id)); }
    else { setSource('file'); setBaseConnId(''); }
  }, [tenantId, baseConns]);
  // T2.1: agencja z jednym klientem — klient wybrany od razu.
  useEffect(() => {
    if (!tenantId && tenants.length === 1) setTenantId(tenants[0].id);
  }, [tenants]);

  // Check active orders on mount (PERF-UX: tylko potrzebne kolumny zamiast `*`; wynik do cache — patrz `activeKey`).
  useEffect(() => {
    let ignore = false;
    (async () => {
      const { data, error } = await supabase
        .from('orders')
        .select('id, marketplace, language, email, product_count, credits_used, status, status_reason, created_at, tenant_id')
        .eq('user_id', user.id)
        .in('status', ['processing', 'queued'])
        .order('created_at', { ascending: true });
      if (ignore) return;
      if (submittedRef.current) { setCheckingActive(false); return; }
      const list = (data || []).map(o => ({
        order_id: o.id,
        marketplace: o.marketplace,
        language: o.language,
        email: o.email,
        product_count: o.product_count,
        credits_used: o.credits_used,
        status: o.status,
        status_reason: o.status_reason,
        created_at: o.created_at,
        tenant_id: o.tenant_id,
      }));
      if (!error) setCached(activeKey, list, { persist: false }); // tylko w pamięci: w obrębie sesji pilnuje go ordersWatch
      // Ten sam zestaw zleceń co z cache → bez nowej tablicy (QueueProgressView nie restartuje odpytywania).
      setActiveJobs(prev => (prev && prev.map(j => `${j.order_id}:${j.status}`).join(',') === list.map(j => `${j.order_id}:${j.status}`).join(',') ? prev : list));
      setCheckingActive(false);
    })();
    return () => { ignore = true; };
    // eslint-disable-next-line
  }, [user.id]);

  if (checkingActive) {
    return (
      <div className="flex items-center justify-center h-64 text-ink-500 dark:text-ink-400 gap-2">
        <Loader2 className="w-4 h-4 animate-spin text-brand-600" />
        <span className="text-sm font-medium">Sprawdzanie aktywnych zleceń…</span>
      </div>
    );
  }

  if (activeJobs && activeJobs.length > 0) {
    return (
      <QueueProgressView
        key={activeJobs.map(j => j.order_id).join(',')}
        activeJobs={activeJobs}
        onOpenReadiness={onOpenReadiness}
        onBalanceChange={refreshBalance}
        onDone={() => {
          setActiveJobs([]);
          setCached(activeKey, [], { persist: false });
          resetForm();
        }}
      />
    );
  }

  const clearProducts = () => { setFile(null); setHeaders([]); setTotalRows(0); setNameCol(''); setDescCol(''); setBaseData(null); setBaseSel([]); };
  const selectTenant = (id) => {
    if (id === tenantId) return;
    clearProducts(); setMpConfig({}); setNoClient(false); setTenantId(id);
  };
  const goNoClient = () => { clearProducts(); setTenantId(''); setNoClient(true); };
  const hasClients = tenants.length > 0;
  // Krok „Produkty” pokazujemy, gdy wiadomo, dla kogo: wybrany klient albo świadomie „bez klienta” (lub agencja bez klientów).
  const whoChosen = !hasClients || noClient || !!tenantId;
  const stepOff = hasClients ? 1 : 0;
  const tenant = tenants.find(t => t.id === tenantId) || null;
  // Nazwy klientów są unikalne tylko w organizacji — admin widzi kilka „Domin”; etykieta musi wskazać jednego.
  const tenantLabel = (t) => (tenants.filter(x => x.name === t.name).length > 1 ? `${t.name} · #${String(t.id).slice(0, 6)}` : t.name);
  const listingVia = (() => {
    const sys = [...new Set(tenantConns.map(c => SYSTEM_LABEL[c.system] || c.system))];
    const direct = tenantChannels.some(ch => ['kaufland', 'allegro', 'amazon'].includes(ch.platform) && !ch.settings?.via_integrator) && tenant?.mode !== 'integrator';
    if (sys.length && direct) return `przez ${sys.join(' i ')} + bezpośrednio na marketplace`;
    if (sys.length) return `przez ${sys.join(' i ')}`;
    return 'bezpośrednio na marketplace';
  })();

  function resetForm() {
    setFile(null); setHeaders([]); setTotalRows(0); setBaseData(null);
    setNameCol(''); setDescCol(''); setMpConfig({});
    setSelectedChannels([]);
  }

  // Derived state — jedna lista zleceń do uruchomienia, z kanałów klienta albo z kafelków marketplace.
  const selectedMarketplaces = Object.keys(mpConfig);
  const jobs = tenantId
    ? tenantChannels
        .filter(ch => selectedChannels.includes(ch.id) && CHANNEL_PLATFORM_TO_MARKETPLACE[ch.platform])
        .map(ch => ({
          marketplace: CHANNEL_PLATFORM_TO_MARKETPLACE[ch.platform],
          language: CHANNEL_LANGUAGE_LABEL[ch.language] || 'Polski',
          tenant_id: tenantId,
          channel_id: ch.id,
          label: `${CHANNEL_PLATFORM_LABEL[ch.platform]} · ${MARKET_LABEL[ch.market] || ch.market.toUpperCase()}`,
        }))
    : selectedMarketplaces.flatMap(mp => (mpConfig[mp] || []).map(lang => ({ marketplace: mp, language: lang, tenant_id: null, channel_id: null, label: `${mp} · ${lang}` })));
  const totalJobs = jobs.length;
  const totalCreditsNeeded = estimateCredits(pricing, { offers: totalRows * totalJobs });
  const notEnoughCredits = totalCreditsNeeded > 0 && totalCreditsNeeded > credits;

  const isReady =
    file && !fileLoading && headers.length > 0 &&
    email && nameCol && descCol &&
    totalJobs > 0 && !notEnoughCredits;

  // Handlers
  const handleFile = async (f) => {
    if (!f) return;
    setFile(f); setHeaders([]); setTotalRows(0);
    setNameCol(''); setDescCol('');
    setFileLoading(true);
    try {
      const { headers: h, rows: r } = await parseFileHeaders(f);
      setHeaders(h); setTotalRows(r);
      const gName = guessColumn(h, NAME_HEADERS);
      setNameCol(gName);
      setDescCol(guessColumn(h, DESC_HEADERS, gName));
      toast.success(`Plik wczytany: ${formatNumber(r)} produktów, ${h.length} kolumn`);
    } catch {
      toast.error('Nie udało się odczytać pliku. Sprawdź format (XLSX lub CSV).');
      setFile(null);
    } finally {
      setFileLoading(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault(); setDragging(false);
    const f = e.dataTransfer.files[0];
    if (f) handleFile(f);
  };

  const toggleMarketplace = (mp) => {
    setMpConfig(prev => {
      const next = { ...prev };
      if (next[mp]) delete next[mp];
      else next[mp] = [];
      return next;
    });
  };

  const setLanguagesFor = (mp, langs) => {
    setMpConfig(prev => ({ ...prev, [mp]: langs }));
  };

  const handleRun = async () => {
    setLoading(true);
    try {
      if (aplusContent && (!aplusImageCol || totalRows > aplusMax)) {
        toast.error(aplusImageCol ? `A+ Content: maksymalnie ${aplusMax} produktów na zlecenie.` : 'A+ Content: wskaż kolumnę ze zdjęciem produktu.');
        setLoading(false); return;
      }
      // Opłata jest naliczana serwerowo w process-order (per order, cena z pricing_config).
      // Klient nie dotyka `credits` — poniższe sprawdzenie służy wyłącznie UX.
      if (totalCreditsNeeded > (credits ?? 0)) {
        toast.error('Niewystarczające kredyty.');
        setLoading(false); return;
      }

      // 3. Parse file locally
      const products = file.virtual && baseData
        ? rowsToProducts(baseData.headers, baseData.rows, nameCol, descCol)
        : await parseFileProducts(file, nameCol, descCol);
      if (!products || products.length === 0) throw new Error('Nie znaleziono produktów w pliku.');

      // Budżet AI (dev): sprawdzamy CAŁĄ kolejkę (produkty × kanały) przed utworzeniem pierwszego zlecenia,
      // żeby nie skończyć z częściowo utworzoną kolejką. Brak funkcji (prod) = brak blokady.
      const { data: verdict } = await supabase.rpc('ai_budget_precheck', { p_products: products.length * jobs.length });
      if (typeof verdict === 'string' && verdict) throw new Error(verdict.replace(/^ai_budget:\s*/, 'Budżet AI — '));

      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Brak sesji — zaloguj się ponownie.');

      const { data: configData } = await supabase.from('config')
        .select('key, value').in('key', ['batch_size', 'products_per_chunk']);
      const configMap = Object.fromEntries((configData || []).map(r => [r.key, r.value]));
      const BATCH_SIZE = parseInt(configMap.batch_size || '2', 10);
      const PRODUCTS_PER_CHUNK = parseInt(configMap.products_per_chunk || '400', 10);
      const BATCHES_PER_CHUNK = Math.max(1, Math.floor(PRODUCTS_PER_CHUNK / BATCH_SIZE));
      const columnOrder = Object.keys(products[0]).filter(k => !['nazwa_clean', 'opis_clean'].includes(k));

      const allBatches = [];
      const totalBatches = Math.ceil(products.length / BATCH_SIZE);
      for (let i = 0; i < totalBatches; i++) {
        allBatches.push({
          batch_index: i,
          total_batches: totalBatches,
          products: products.slice(i * BATCH_SIZE, (i + 1) * BATCH_SIZE),
        });
      }

      const batchChunks = [];
      for (let i = 0; i < allBatches.length; i += BATCHES_PER_CHUNK) {
        batchChunks.push(allBatches.slice(i, i + BATCHES_PER_CHUNK));
      }

      const createdJobs = [];
      submittedRef.current = true;
      // v9/C1: jedno wgranie na kilka kanałów → wspólny upload_id (zapis do BASE wszystkich kanałów naraz).
      const uploadId = crypto.randomUUID();

      for (const job of jobs) {
        {
          const mp = job.marketplace, lang = job.language;
          const order_id = crypto.randomUUID();

          const sendChunk = async (chunk, isFirst, isLast) => {
            const res = await fetch(PROCESS_ORDER_URL, {
              method: 'POST',
              // apikey + JWT usera: od D34 funkcja ma verify_jwt=true (gateway) i sprawdza wlasciciela zlecenia.
              headers: {
                'Content-Type': 'application/json',
                'apikey': SUPABASE_ANON_KEY,
                'Authorization': `Bearer ${session.access_token}`,
              },
              body: JSON.stringify({
                order_id, batches: chunk, is_first: isFirst, is_last: isLast,
                total_products: products.length, total_batches: totalBatches,
                marketplace: mp, language: lang,
                tenant_id: job.tenant_id || undefined, channel_id: job.channel_id || undefined,
                upload_id: isFirst && jobs.length > 1 ? uploadId : undefined,
                name_column: nameCol, desc_columns: [descCol],
                email, filename: file.name,
                column_order: isFirst ? columnOrder : undefined,
                aplus_content: isFirst ? (aplusContent && !!aplusImageCol) : undefined,
                aplus_image_column: isFirst && aplusContent ? aplusImageCol : undefined,
              }),
            });
            if (!res.ok) {
              const errData = await res.json().catch(() => ({}));
              if (res.status === 402) throw new Error('Niewystarczające kredyty.');
              // Blokada budżetu AI (trigger before_order_ai_budget, dev): pokaż komunikat z bazy zamiast „Failed to create order”.
              const budget = String(errData.detail || '').match(/ai_budget:\s*(.+)$/);
              if (budget) throw new Error(`Budżet AI — ${budget[1]}`);
              throw new Error(errData.error || `Błąd wysyłania zlecenia (${mp} / ${lang})`);
            }
            return res.json();
          };

          const firstRes = await sendChunk(batchChunks[0], true, batchChunks.length === 1);
          for (let c = 1; c < batchChunks.length; c += 5) {
            const group = batchChunks.slice(c, c + 5);
            await Promise.all(group.map((chunk, i) =>
              sendChunk(chunk, false, c + i === batchChunks.length - 1)
            ));
          }

          createdJobs.push({
            order_id, marketplace: mp, language: lang, email, tenant_id: job.tenant_id || null,
            product_count: totalRows, credits_used: totalRows,
            status: firstRes.queued ? 'queued' : 'processing',
            created_at: new Date().toISOString(),
          });
        }
      }

      await refreshBalance();
      toast.success(`Uruchomiono ${createdJobs.length} ${createdJobs.length === 1 ? 'zlecenie' : 'zleceń'}`);
      setActiveJobs(createdJobs);
      // Listy zleceń (Gotowość, Historia) od razu nieaktualne → odświeżą się; globalny nasłuch pilnuje końca biegu.
      notifyOrdersChanged({ created: createdJobs.map(j => j.order_id) });
      setCached(activeKey, createdJobs, { persist: false }); // po unieważnieniu wyżej — następne wejście od razu pokaże tę kolejkę
      kickOrdersWatch(createdJobs.map(j => j.order_id));
      setLoading(false);
    } catch (err) {
      // Zlecenia utworzone przed błędem zostają opłacone i przetwarzane — process-order
      // zwraca kredyty tylko za order, którego nie udało się w pełni założyć.
      await refreshBalance();
      toast.error(`Błąd: ${err.message}`);
      setLoading(false);
    }
  };

  return (
    <div className="animate-fade-in pb-32">
      <PageHeader
        title="Nowe zlecenie"
        subtitle="Wybierz klienta, pobierz jego katalog albo wgraj plik — MAMS stworzy oferty na jego kanały."
      />

      {/* Krok 1 — Klient (życzenie Maćka 18.10: najpierw klient, program sam wie, jak go wystawiamy) */}
      {hasClients && (
        <Card className="mb-5">
          <CardHeader>
            <div className="flex items-center gap-3">
              <StepBadge n={1} done={whoChosen} />
              <div>
                <CardTitle>Dla którego klienta?</CardTitle>
                <CardDescription>Po wyborze MAMS sam wczyta, jak ten klient jest wystawiany (BaseLinker / Sellasist / bezpośrednio) i jego kanały.</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardBody>
            {noClient ? (
              <div className="flex flex-wrap items-center justify-between gap-3 text-sm">
                <span className="text-ink-700 dark:text-ink-300"><b>Bez klienta</b> — wgrasz plik XLSX/CSV i wybierzesz marketplace ręcznie (testy, jednorazowe zlecenia).</span>
                <Button size="sm" variant="secondary" onClick={() => { clearProducts(); setMpConfig({}); setNoClient(false); }}>Wybierz klienta</Button>
              </div>
            ) : (
              <div className="grid gap-3">
                <div className="max-w-md">
                  <Label htmlFor="tenant">Klient</Label>
                  <Select
                    value={tenant ? tenantLabel(tenant) : ''}
                    onChange={(label) => { const t = tenants.find(x => tenantLabel(x) === label); if (t) selectTenant(t.id); }}
                    options={tenants.map(tenantLabel)}
                    placeholder="Wybierz klienta…"
                  />
                </div>
                {tenant && (
                  <div className="flex flex-wrap gap-2 text-xs">
                    <span className="rounded-md bg-ink-100 dark:bg-ink-800 px-2 py-1">Wystawianie: <b>{listingVia}</b></span>
                    <span className="rounded-md bg-ink-100 dark:bg-ink-800 px-2 py-1">Kanały: <b>{tenantChannels.filter(ch => CHANNEL_PLATFORM_TO_MARKETPLACE[ch.platform]).length}</b> włączonych</span>
                    <span className="rounded-md bg-ink-100 dark:bg-ink-800 px-2 py-1">Produkty: <b>{tenantConns.length ? `z ${[...new Set(tenantConns.map(c => SYSTEM_LABEL[c.system] || c.system))].join(' / ')} albo z pliku` : 'z pliku XLSX / CSV'}</b></span>
                  </div>
                )}
                <button type="button" onClick={goNoClient} className="justify-self-start text-left text-xs text-ink-500 hover:text-ink-800 dark:text-ink-400 dark:hover:text-ink-100 underline underline-offset-2">
                  Zlecenie bez klienta — zwykły plik XLSX/CSV
                </button>
              </div>
            )}
          </CardBody>
        </Card>
      )}

      {/* Krok 2 — Produkty: katalog klienta z integratora albo plik */}
      {whoChosen && (
      <Card className="mb-5 animate-fade-in">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <StepBadge n={1 + stepOff} done={!!file && !fileLoading} />
              <div>
                <CardTitle>{source === 'base' ? `Katalog klienta z ${sysLabel}` : 'Wgraj plik z produktami'}</CardTitle>
                <CardDescription>{source === 'base' ? 'Wybierz gałąź katalogu — produkty pobiorą się same' : 'Format XLSX lub CSV · pierwszy wiersz = nagłówki kolumn'}</CardDescription>
              </div>
            </div>
          </div>
        </CardHeader>
        <CardBody>
          {!file && tenantConns.length > 0 && (
            <div className="mb-4 inline-flex flex-wrap p-1 bg-ink-100 dark:bg-ink-800 rounded-md gap-1">
              {[...tenantConns.map(c => [c.id, `Katalog: ${connLabel(c)}`]), ['file', 'Plik XLSX / CSV']].map(([id, label]) => {
                const on = id === 'file' ? source === 'file' : source === 'base' && baseConnId === id;
                return (
                  <button key={id} type="button" onClick={() => { if (id === 'file') setSource('file'); else { setSource('base'); setBaseConnId(id); } }}
                    className={cn('px-3 h-8 rounded text-sm font-medium transition-all', on ? 'bg-white text-ink-900 shadow-sm dark:bg-ink-900 dark:text-ink-50' : 'text-ink-600 hover:text-ink-900 dark:text-ink-400 dark:hover:text-ink-100')}>{label}</button>
                );
              })}
            </div>
          )}
          {!file && source === 'base' ? (
            <div className="grid gap-4">
              {baseConnId && (baseBusy && !baseCats ? (
                <div className="text-sm text-ink-500 dark:text-ink-400 inline-flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Pobieram drzewo kategorii…</div>
              ) : baseCats && (
                <div>
                  <Label>Gałęzie katalogu {baseCats.categories.length === 0 && <span className="font-normal text-ink-400">— katalog bez kategorii, pobierzemy wszystko</span>}</Label>
                  {baseCats.categories.length > 0 && (
                    <div className="max-h-72 overflow-y-auto rounded-lg border border-ink-200 dark:border-ink-700 divide-y divide-ink-100 dark:divide-ink-800">
                      {baseCats.categories.slice().sort((a, b) => a.path.localeCompare(b.path, 'pl')).map(c => {
                        const depth = c.path.split(' > ').length - 1;
                        const on = baseSel.includes(c.id);
                        const n = baseCats.counts?.[String(c.id)];
                        return (
                          <label key={c.id} className="flex items-center gap-2 px-3 py-2 text-sm cursor-pointer hover:bg-ink-50 dark:hover:bg-ink-800/50" style={{ paddingLeft: 12 + depth * 18 }}>
                            <input type="checkbox" checked={on} onChange={() => setBaseSel(prev => on ? prev.filter(x => x !== c.id) : [...prev, c.id])} className="w-4 h-4 accent-brand-600" />
                            <span className="flex-1 text-ink-800 dark:text-ink-200">{c.name}</span>
                            {n != null && <span className="text-2xs text-ink-400">{n}{baseCats.sampled >= 1000 ? '+' : ''}</span>}
                          </label>
                        );
                      })}
                    </div>
                  )}
                  <div className="mt-3 flex items-center gap-3">
                    <Button onClick={fetchBaseProducts} loading={baseBusy} leftIcon={<ArrowRight className="w-4 h-4" />}>
                      {baseSel.length ? `Pobierz produkty z ${baseSel.length} ${baseSel.length === 1 ? 'gałęzi' : 'gałęzi'}` : 'Pobierz cały katalog'}
                    </Button>
                    <span className="text-xs text-ink-500 dark:text-ink-400">Zaznaczona gałąź obejmuje podkategorie. Limit 5 000 produktów na zlecenie.</span>
                  </div>
                </div>
              ))}
            </div>
          ) : !file ? (
            <div
              onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
              onDragLeave={() => setDragging(false)}
              onDrop={handleDrop}
              onClick={() => fileRef.current?.click()}
              className={cn(
                'relative rounded-xl border-2 border-dashed p-10 text-center cursor-pointer transition-all',
                dragging
                  ? 'border-brand-500 bg-brand-50/60 dark:bg-brand-900/20 scale-[1.005]'
                  : 'border-ink-300 bg-ink-50/40 hover:border-brand-400 hover:bg-brand-50/30 dark:border-ink-700 dark:bg-ink-800/30 dark:hover:border-brand-700 dark:hover:bg-brand-900/10'
              )}
            >
              <div className={cn(
                'mx-auto mb-4 w-14 h-14 rounded-2xl flex items-center justify-center transition-all',
                dragging
                  ? 'bg-brand-600 text-white scale-110'
                  : 'bg-white border border-ink-200 text-brand-600 shadow-sm dark:bg-ink-900 dark:border-ink-700 dark:text-brand-400'
              )}>
                <Upload className="w-6 h-6" strokeWidth={2.2} />
              </div>
              <div className="text-base font-semibold text-ink-900 dark:text-ink-50 mb-1">
                {dragging ? 'Upuść plik tutaj' : 'Przeciągnij plik lub kliknij, aby wybrać'}
              </div>
              <div className="text-sm text-ink-500 dark:text-ink-400">XLSX · CSV · do 200 MB</div>
              <input ref={fileRef} type="file" accept=".xlsx,.csv" className="hidden" onChange={(e) => handleFile(e.target.files[0])} />
            </div>
          ) : (
            <div className="flex items-center gap-3 p-4 rounded-lg bg-success-light/40 dark:bg-success/10 border border-success/20">
              <div className="w-10 h-10 rounded-lg bg-white dark:bg-ink-900 border border-success/20 flex items-center justify-center text-success-dark dark:text-success flex-shrink-0">
                <FileSpreadsheet className="w-5 h-5" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-ink-900 dark:text-ink-50 truncate">{file.name}</div>
                <div className="text-xs text-ink-600 dark:text-ink-400 mt-0.5">
                  {fileLoading ? (
                    <span className="inline-flex items-center gap-1.5">
                      <Loader2 className="w-3 h-3 animate-spin" />
                      Wczytywanie kolumn…
                    </span>
                  ) : (
                    <>{formatNumber(totalRows)} produktów · {headers.length} kolumn</>
                  )}
                </div>
              </div>
              <button
                onClick={clearProducts}
                className="p-1.5 rounded-md text-ink-400 dark:text-ink-500 hover:text-danger hover:bg-danger-light/50 dark:hover:bg-danger/15 transition-colors"
                title="Usuń plik"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          )}
        </CardBody>
      </Card>
      )}

      {/* Step 2 — Mapping */}
      {file && !fileLoading && headers.length > 0 && (
        <Card className="mb-5 animate-fade-in">
          <CardHeader>
            <div className="flex items-center gap-3">
              <StepBadge n={2 + stepOff} done={!!nameCol && !!descCol && !!email} />
              <div>
                <CardTitle>Kolumny i powiadomienie</CardTitle>
                <CardDescription>{nameCol && descCol ? 'Kolumny z nazwą i opisem rozpoznane z nagłówków — sprawdź i zmień, jeśli trzeba' : 'Wskaż kolumny z nazwą i opisem produktu'}</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardBody>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <div>
                <Label htmlFor="name-col" required>Kolumna z nazwą</Label>
                <Select value={nameCol} onChange={setNameCol} options={headers} placeholder="Wybierz kolumnę…" />
              </div>
              <div>
                <Label htmlFor="desc-col" required>Kolumna z opisem</Label>
                <Select value={descCol} onChange={setDescCol} options={headers} placeholder="Wybierz kolumnę…" />
              </div>
              <div>
                <Label htmlFor="email" required>E-mail na powiadomienie „zlecenie gotowe”</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="ty@firma.pl"
                  leftIcon={<Mail className="w-4 h-4" />}
                />
              </div>
            </div>
          </CardBody>
        </Card>
      )}

      {/* A+ Content (beta) */}
      {file && !fileLoading && headers.length > 0 && (
        <Card className="mb-5 animate-fade-in">
          <CardHeader>
            <div className="flex items-center gap-3">
              <Sparkles className="w-5 h-5 text-brand-600" />
              <div>
                <CardTitle>A+ Content (beta)</CardTitle>
                <CardDescription>Wygeneruj 3 grafiki A+ na produkt ze zdjęcia (hero · w użyciu · cechy). Maks. {aplusMax} produktów na zlecenie.</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardBody>
            <label className="flex items-center gap-3 cursor-pointer select-none">
              <input type="checkbox" checked={aplusContent} onChange={(e) => setAplusContent(e.target.checked)} className="w-4 h-4 accent-brand-600" />
              <span className="text-sm font-medium text-ink-800 dark:text-ink-200">Generuj A+ Content dla tego zlecenia</span>
            </label>
            {aplusContent && (
              <div className="mt-4 max-w-md">
                <Label htmlFor="aplus-col" required>Kolumna ze zdjęciem produktu (URL)</Label>
                <Select value={aplusImageCol} onChange={setAplusImageCol} options={headers} placeholder="Wybierz kolumnę…" />
                {totalRows > aplusMax && (
                  <p className="mt-2 text-xs text-red-600">Plik ma {totalRows} produktów — A+ obsługuje maks. {aplusMax} na zlecenie. Wgraj mniejszy plik lub odznacz A+.</p>
                )}
              </div>
            )}
          </CardBody>
        </Card>
      )}

      {/* Step 3 — Klient i kanały (K2) albo marketplace'y i języki */}
      {file && !fileLoading && headers.length > 0 && (
        <Card className="mb-5 animate-fade-in">
          <CardHeader>
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <StepBadge n={3 + stepOff} done={totalJobs > 0} />
                <div>
                  <CardTitle>{tenantId ? `Kanały klienta ${tenant?.name || ''}` : "Marketplace'y i języki"}</CardTitle>
                  <CardDescription>
                    {tenantId
                      ? 'Oferty pójdą na konto klienta. Włączone kanały są zaznaczone — odznacz, czego nie chcesz.'
                      : 'Kliknij kafelek, aby wybrać marketplace, potem wskaż dla niego języki'}
                  </CardDescription>
                </div>
              </div>
              {totalJobs > 0 && (
                <Badge variant="brand" size="md">
                  {totalJobs} {totalJobs === 1 ? 'zlecenie' : totalJobs < 5 ? 'zlecenia' : 'zleceń'}
                </Badge>
              )}
            </div>
          </CardHeader>
          <CardBody>
            {tenantId ? (
              tenantChannels.length === 0 ? (
                <p className="text-sm text-ink-500 dark:text-ink-400">Ten klient nie ma włączonych kanałów sprzedaży. Dodaj je w zakładce Klienci (karta klienta → kanały).</p>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {tenantChannels.map(ch => {
                    const runnable = !!CHANNEL_PLATFORM_TO_MARKETPLACE[ch.platform];
                    const on = selectedChannels.includes(ch.id);
                    return (
                      <button
                        key={ch.id}
                        type="button"
                        disabled={!runnable}
                        onClick={() => setSelectedChannels(prev => on ? prev.filter(id => id !== ch.id) : [...prev, ch.id])}
                        className={cn(
                          'flex items-center gap-3 p-3.5 rounded-lg border text-left transition-colors',
                          !runnable ? 'border-ink-100 dark:border-ink-800 opacity-60 cursor-not-allowed'
                            : on ? 'border-brand-500 bg-brand-50 dark:bg-brand-900/30 dark:border-brand-600'
                            : 'border-ink-200 hover:border-ink-300 dark:border-ink-700 dark:hover:border-ink-600',
                        )}
                      >
                        <span className={cn('w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0', on ? 'bg-brand-600 border-brand-600' : 'border-ink-300 dark:border-ink-600')}>
                          {on && <Check className="w-3.5 h-3.5 text-white" strokeWidth={3} />}
                        </span>
                        {runnable && <MarketplaceLogo name={CHANNEL_PLATFORM_TO_MARKETPLACE[ch.platform]} size={32} />}
                        <span className="min-w-0">
                          <span className="block text-sm font-semibold text-ink-900 dark:text-ink-50">{CHANNEL_PLATFORM_LABEL[ch.platform] || ch.platform} · {MARKET_LABEL[ch.market] || ch.market.toUpperCase()}</span>
                          <span className="block text-xs text-ink-500 dark:text-ink-400">
                            {runnable ? `treść: ${CHANNEL_LANGUAGE_LABEL[ch.language] || ch.language}${(ch.settings?.via_integrator || tenants.find(t => t.id === tenantId)?.mode === 'integrator') ? ' · zapis do BaseLinker/Sellasist klienta (wystawia integrator)' : ''}` : 'to źródło katalogu, nie kanał sprzedaży — w Klienci włącz np. „Kaufland · Polska (przez integrator)"'}
                          </span>
                        </span>
                      </button>
                    );
                  })}
                </div>
              )
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 xl:grid-cols-2 gap-3">
                {ALL_MARKETPLACES.map(mp => (
                  <MarketplaceCard
                    key={mp}
                    name={mp}
                    selected={mpConfig[mp] !== undefined}
                    languages={mpConfig[mp] || []}
                    onToggle={() => toggleMarketplace(mp)}
                    onLanguagesChange={(langs) => setLanguagesFor(mp, langs)}
                  />
                ))}
              </div>
            )}
          </CardBody>
        </Card>
      )}

      {/* Sticky pricing + submit bar */}
      {file && !fileLoading && headers.length > 0 && (
        <div className="fixed bottom-0 left-[248px] right-0 bg-white/85 dark:bg-ink-900/85 backdrop-blur-md border-t border-ink-200/60 dark:border-ink-800/60 shadow-2xl shadow-brand-600/5 z-30">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-3.5 flex items-center justify-between gap-4">
            <div className="flex items-center gap-5 min-w-0 flex-wrap">
              <PricingStat
                icon={<Sparkles className="w-3.5 h-3.5" />}
                label="Generations"
                value={totalJobs > 0 ? formatNumber(totalRows * totalJobs) : '—'}
                sub={totalJobs > 0 ? `${formatNumber(totalRows)} × ${totalJobs} ${totalJobs === 1 ? 'zlec.' : 'zleceń'}` : (tenantId ? 'zaznacz kanał' : 'wybierz marketplace')}
              />
              <Divider />
              <PricingStat
                icon={<Info className="w-3.5 h-3.5" />}
                label="Koszt"
                value={totalCreditsNeeded > 0 ? `${formatNumber(totalCreditsNeeded)} kredytów` : '—'}
                sub={`zostanie ${formatNumber(Math.max(0, (credits ?? 0) - totalCreditsNeeded))}`}
                tone={notEnoughCredits ? 'danger' : 'neutral'}
              />
              {notEnoughCredits && (
                <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-danger-dark dark:text-danger">
                  <AlertTriangle className="w-3.5 h-3.5" />
                  Brakuje {formatNumber(totalCreditsNeeded - (credits ?? 0))} kredytów
                </span>
              )}
            </div>
            <Button
              size="lg"
              onClick={handleRun}
              loading={loading}
              disabled={!isReady}
              leftIcon={!loading && <Play className="w-4 h-4" />}
            >
              {loading
                ? 'Uruchamianie…'
                : totalJobs > 1
                  ? `Uruchom kolejkę (${totalJobs})`
                  : 'Uruchom proces'}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function StepBadge({ n, done }) {
  return (
    <div className={cn(
      'w-8 h-8 rounded-lg flex items-center justify-center text-sm font-bold flex-shrink-0 transition-all',
      done ? 'bg-success text-white' : 'bg-brand-50 text-brand-700 ring-1 ring-inset ring-brand-200/60'
    )}>
      {done ? <Check className="w-4 h-4" strokeWidth={3} /> : n}
    </div>
  );
}

function PricingStat({ icon, label, value, sub, tone = 'neutral' }) {
  return (
    <div className="min-w-0">
      <div className={cn(
        'flex items-center gap-1.5 text-2xs uppercase tracking-wide font-bold mb-0.5',
        tone === 'danger' ? 'text-danger-dark dark:text-danger' : 'text-ink-500 dark:text-ink-400'
      )}>
        {icon}
        {label}
      </div>
      <div className={cn(
        'text-sm font-bold tracking-tightish whitespace-nowrap',
        tone === 'danger' ? 'text-danger-dark dark:text-danger' : 'text-ink-900 dark:text-ink-50'
      )}>
        {value}
      </div>
      {sub && <div className="text-2xs text-ink-500 dark:text-ink-400 mt-0.5">{sub}</div>}
    </div>
  );
}

function Divider() {
  return <span className="w-px h-9 bg-ink-200 dark:bg-ink-800 hidden sm:block" />;
}
